import type { ReactNode } from 'react';
import { cn } from './format';

export type AvatarProps = {
  name: string;
  src?: string | null;
  size?: 'sm' | 'md' | 'lg';
};

const sizes = { sm: 'h-7 w-7 text-xs', md: 'h-9 w-9 text-sm', lg: 'h-12 w-12 text-base' };

function initials(name: string) {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function Avatar({ name, src, size = 'md' }: AvatarProps) {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('rounded-full object-cover', sizes[size])}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-[var(--accent)]/25 font-semibold text-[var(--accent)]',
        sizes[size],
      )}
    >
      {initials(name)}
    </span>
  );
}

export type StatusPillProps = {
  label: string;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
};

const tones: Record<NonNullable<StatusPillProps['tone']>, string> = {
  neutral: 'bg-[var(--bg-panel)] text-[var(--text-muted)]',
  success: 'bg-[var(--success-dim)] text-[var(--success)]',
  warning: 'bg-[rgba(245,165,36,0.15)] text-[var(--warning)]',
  danger: 'bg-[rgba(240,113,120,0.15)] text-[var(--danger)]',
  info: 'bg-[rgba(26,155,140,0.15)] text-[var(--accent)]',
};

export function StatusPill({ label, tone = 'neutral' }: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        tones[tone],
      )}
    >
      {label}
    </span>
  );
}

export type ModalProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
};

export function Modal({ open, title, onClose, children, footer }: ModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-black/60" aria-hidden onClick={onClose} />
      <div className="relative z-10 flex max-h-[min(92vh,40rem)] w-full max-w-lg flex-col overflow-hidden rounded-[var(--radius)] border border-[var(--border)] bg-[var(--bg-elevated)] shadow-2xl">
        <header className="flex shrink-0 items-center justify-between border-b border-[var(--border-subtle)] px-4 py-2.5">
          <h2 className="m-0 text-sm font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-[var(--text-muted)] hover:bg-[var(--bg-hover)]"
            aria-label="Close"
          >
            ✕
          </button>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">{children}</div>
        {footer && (
          <footer className="flex shrink-0 justify-end gap-2 border-t border-[var(--border-subtle)] px-4 py-2.5">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}

export type DropdownOption = { value: string; label: string };

export type DropdownProps = {
  label?: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
};

export function Dropdown({
  label,
  value,
  options,
  onChange,
  placeholder = 'All',
  className,
}: DropdownProps) {
  return (
    <label className={cn('flex flex-col gap-1 text-sm', className)}>
      {label && <span className="text-[var(--text-muted)]">{label}</span>}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-lg border border-[var(--border)] bg-[var(--bg-panel)] px-3 text-[var(--text)]"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
